/**
 * Pure AG-UI → draft reducer for Copilot chat bubbles.
 *
 * AG-UI streams a flat list of protocol messages (user / assistant / tool /
 * reasoning). The chat UI needs a coarser shape: one bubble per user turn, and
 * one “copilot” bubble that may contain several ordered content parts. This
 * module collapses that stream into serializable drafts; the React adapter
 * (`useCreateMessagesFromAgUiMessages`) turns drafts into JSX.
 *
 * ## Output shape
 *
 * - `user` — one draft per user message (flush any open copilot group first).
 * - `copilot` — one draft that accumulates parts until the next user message
 *   (or end of stream). Parts are one of:
 *   - `thinkingSignatures` — accordion rows for inspect/search/data tools
 *   - `assistantContent` — markdown / generative UI for a given assistant msg
 *   - `visibleTool` — standalone cards (highlight, plan approval, …)
 *
 * ## Why we merge
 *
 * A single agent turn is often many AG-UI messages: several assistant messages
 * with toolCalls, matching `role: 'tool'` results, then prose. Showing each as
 * its own bubble would fragment the turn. Instead we keep an open **copilot
 * group** and append parts into it.
 *
 * **Thinking signatures** from consecutive tool-using assistants are buffered
 * in `pendingThinkingSignatures`, then merged into the last
 * `thinkingSignatures` part (or a new one) when we flush — so “Explored …”
 * stays one accordion rather than N tiny ones.
 *
 * **Charts** from tool calls/results accumulate in `chartIdMap` and are
 * snapshotted onto the next `assistantContent` part (markdown chart tokens
 * resolve against that map later).
 *
 * ## Role handling (walk order)
 *
 * | AG-UI role                         | Effect |
 * | ---------------------------------- | ------ |
 * | `user`                             | Flush group → emit user draft |
 * | `assistant` (+ toolCalls)          | Open/continue group; extract thinking + charts; if there is text/generative UI, flush pending thinking then append `assistantContent` |
 * | `tool`                             | Attach chart data; if the matching tool is “visible”, enqueue/render a `visibleTool` part |
 * | `reasoning`                        | Concatenate consecutive messages into one `thought` signature (detail joined by `\\n`); duration = first reasoning → next non-reasoning |
 * | `assistant` `coagent-state-render` | Ignored |
 *
 * ## ADK trailing reasoning quirk
 *
 * Google ADK (backend) often emits a final `REASONING_MESSAGE_*` burst *after*
 * `TEXT_MESSAGE_END` for the same turn. Walk order would otherwise place that
 * thought *below* the assistant prose as a second block. When the turn has
 * settled and the bubble ends with thought-only signatures after
 * `assistantContent`, we hoist them above the last prose part (see
 * `hoistTrailingAdkReasoningThoughts`). Overlapping/replayed ADK text merges into
 * the last thought; distinct text is appended so Thought → Tool → Thought stays
 * interleaved. Settled trailing streaks close at their last reasoning event —
 * `now` is only the live timer for the **open** (final) group while `isLoading`.
 * Conversation-wide `isLoading` must not mark earlier turns mid-think or skip
 * hoist when a later user message flushes those groups (`settled: true`).
 *
 * ## Visible tools & `message_end`
 *
 * Tools marked `visible` in the registry become `visibleTool` parts.
 * Placement `inline` (default) inserts when the tool result arrives.
 * Placement `message_end` (e.g. plan approval, suggest datasource, change_url) is
 * held in `pendingMessageEndToolCallIds` until the copilot bubble flushes — and
 * while `isLoading` is true we flush **without** those parts so cards do not
 * appear mid-stream above unfinished prose. After the run ends, a final flush
 * attaches them at the bottom of the bubble. Every unsettled `message_end` call
 * gets its own card — same-name duplicates must not be hidden, or confirming the
 * visible card would resume the agent while an earlier call stays pending.
 *
 * Exception: once a HITL tool result is terminal (`approved` / `rejected` /
 * `failed`), the card is treated as inline so a follow-up agent run does not
 * hide the settled card and re-append it after new prose.
 *
 * ## Loading / empty trailing bubble
 *
 * If the stream is still loading and the last output is not already a copilot
 * draft, we append an empty copilot draft with `isThinkingInProgress` so the
 * UI can show a thinking indicator before any parts exist.
 */

import type { CopilotThinkingSignature } from '@precis-digital/kurama/MessageLayout'
import type { Theme } from '@precis-digital/kurama/theme'
import { type Message as AgUiMessage, type ToolCall as AgUiToolCall } from '@ag-ui/client'
import { type HydratedAgUiMessage } from 'shared/api/copilot'
import {
  addChartToMap,
  extractChartFromToolCall,
  extractChartFromToolResult
} from 'shared/copilot/charts/extractCopilotChart'
import { type CopilotChart } from 'shared/copilot/charts/dataChartTypes'
import { extractDataChartFromToolResult } from 'shared/copilot/charts/extractDataChart'
import {
  extractThinkingSignatureFromToolCall,
  getCopilotVisibleToolPlacement,
  isCopilotVisibleTool,
  noteUnhandledCopilotTool
} from 'shared/copilot/toolRegistry'
import type { CopilotPageElement } from '../pageContext/types'
import {
  extractAttachedCsvFromUserContent,
  extractUserMessageText,
  resolveSessionRestoreFilename
} from 'shared/copilot/utils/csvAttachment'
import { extractAttachedImagesFromUserContent, type AttachedMessageImage } from 'shared/copilot/utils/imageAttachment'

type ToolResultMessage = AgUiMessage & { role: 'tool'; toolCallId: string }
type AssistantAgUiMessage = AgUiMessage & { role: 'assistant' }
type ReasoningAgUiMessage = AgUiMessage & { role: 'reasoning'; content?: string }

interface OpenReasoningStreak {
  id: string
  contentSegments: string[]
  startedAt: Date
  /**
   * Latest event clock for this streak. Prefer a frozen end time from
   * `reasoningEndedAtById` when the live run has settled; otherwise the message
   * arrival/`createdAt` stamp. Live open flushes use `now` instead of this field.
   */
  lastEventAt: Date
}

/** Duration for a thought signature from streak start to the ending event (or `now` while still open). */
export const reasoningStreakDurationSeconds = (startedAt: Date, endedAt: Date): number =>
  Math.max(0, Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000))

const reasoningMessageText = (message: ReasoningAgUiMessage): string =>
  typeof message.content === 'string' ? message.content : ''

type AssistantMessageWithGenerativeUi = AssistantAgUiMessage & {
  generativeUI?: () => unknown
  generativeUIPosition?: 'before' | 'after'
}

export type AgUiContentPartDraft =
  | {
      type: 'thinkingSignatures'
      id: string
      thinkingSignatures: CopilotThinkingSignature[]
      isThinkingInProgress?: boolean
    }
  | {
      type: 'assistantContent'
      id: string
      messageId: string
      chartEntries: Array<[string, CopilotChart]>
    }
  | {
      type: 'visibleTool'
      id: string
      toolCallId: string
    }

export type AgUiMessageDraft =
  | {
      kind: 'user'
      id: string
      createdAtIso: string
      content: string
      attachedCsvFilename: string | null
      /**
       * Raw URI behind `attachedCsvFilename` — kept alongside the resolved name (which is
       * always non-null once a CSV is attached, even a raw file id, so the tile never
       * silently disappears) so the component can look the URI up live against the CSV
       * index and tell "no longer listed there" apart from "no CSV was attached".
       */
      attachedCsvGcsUri: string | null
      attachedImages: AttachedMessageImage[]
    }
  | {
      kind: 'copilot'
      id: string
      createdAtIso: string
      contentParts: AgUiContentPartDraft[]
      isThinkingInProgress?: boolean
    }

interface ReduceAgUiMessagesArgs {
  messages: AgUiMessage[]
  isLoading: boolean
  pageElements: CopilotPageElement[] | undefined
  theme: Theme
  /** Wall-clock for live messages without createdAt (injectable for tests). */
  now?: Date
  /** Maps persisted CSV `gcsUri` values to human-readable filenames for history chips. */
  csvFilenameByGcsUri?: Readonly<Record<string, string>>
  /**
   * Frozen end times for reasoning ids after a live run settles. Prevents
   * reasoning-only / stop from snapping thought duration to 0 on re-render.
   */
  reasoningEndedAtById?: ReadonlyMap<string, string>
}

const getMessageDate = (message: HydratedAgUiMessage, now: Date): Date => {
  const ts = message.createdAt
  return ts != null ? new Date(ts) : now
}

const isAssistantMessageWithGenerativeUi = (message: AgUiMessage): message is AssistantMessageWithGenerativeUi =>
  message.role === 'assistant'

const findLastVisibleAgUiMessageId = (messages: AgUiMessage[]): string | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role === 'tool') continue
    if (m.role === 'reasoning') continue
    if (m.role === 'assistant' && m.name === 'coagent-state-render') continue
    return m.id
  }
  return null
}

const getToolCallsById = (messages: AgUiMessage[]): Record<string, AgUiToolCall> => {
  return messages.reduce<Record<string, AgUiToolCall>>((acc, message) => {
    if (message.role !== 'assistant') return acc

    message.toolCalls?.forEach((toolCall) => {
      acc[toolCall.id] = toolCall
    })

    return acc
  }, {})
}

const getToolCallResultsById = (messages: AgUiMessage[]): Record<string, ToolResultMessage> => {
  return messages
    .filter((message): message is ToolResultMessage => message.role === 'tool')
    .reduce<Record<string, ToolResultMessage>>((acc, message) => {
      acc[message.toolCallId] = message
      return acc
    }, {})
}

const extractThinkingSignatures = (
  toolCalls: AgUiToolCall[],
  toolCallResultsMap: Record<string, ToolResultMessage>,
  pageElements: CopilotPageElement[] | undefined,
  theme: Theme
): CopilotThinkingSignature[] => {
  return toolCalls.flatMap((toolCall) => {
    const resultMessage = toolCallResultsMap[toolCall.id]
    const hasResult = resultMessage != null
    const toolResultContent = typeof resultMessage?.content === 'string' ? resultMessage.content : undefined
    const signature = extractThinkingSignatureFromToolCall(toolCall, hasResult, pageElements, theme, toolResultContent)
    return signature == null ? [] : [signature]
  })
}

const hasPendingToolResults = (
  toolCalls: AgUiToolCall[],
  toolCallResultsMap: Record<string, ToolResultMessage>
): boolean => toolCalls.some((toolCall) => !(toolCall.id in toolCallResultsMap))

const hasAssistantContentPart = (message: AssistantMessageWithGenerativeUi): boolean => {
  const content = message.content == null ? '' : String(message.content)
  if (content.trim().length > 0) return true
  if ((message.toolCalls ?? []).length > 0) return false
  return message.generativeUI != null
}

const thinkingPartId = (signatures: CopilotThinkingSignature[]): string =>
  `thinking-${signatures.map((signature) => signature.id).join('-')}`

/**
 * Clears `isThinkingInProgress` on thinking-signature parts once the agent moves on
 * to non-thinking content (assistant text, visible tools, etc.).
 */
export const clearThinkingSignaturesInProgress = (contentParts: AgUiContentPartDraft[]): AgUiContentPartDraft[] =>
  contentParts.map((part) => {
    if (part.type !== 'thinkingSignatures' || part.isThinkingInProgress !== true) {
      return part
    }

    return {
      type: 'thinkingSignatures',
      id: part.id,
      thinkingSignatures: part.thinkingSignatures
    }
  })

/**
 * Immutably merges pending thinking signatures into content parts.
 * Extends the last thinkingSignatures part when present; otherwise appends a new one.
 */
export const mergePendingThinkingSignatures = (
  contentParts: AgUiContentPartDraft[],
  pendingThinkingSignatures: CopilotThinkingSignature[],
  isThinkingInProgress = false
): AgUiContentPartDraft[] => {
  if (pendingThinkingSignatures.length === 0) {
    return contentParts
  }

  const lastContentPart = contentParts.at(-1)
  if (lastContentPart?.type === 'thinkingSignatures') {
    const thinkingSignatures = [...lastContentPart.thinkingSignatures, ...pendingThinkingSignatures]
    return [
      ...contentParts.slice(0, -1),
      {
        type: 'thinkingSignatures',
        id: thinkingPartId(thinkingSignatures),
        thinkingSignatures,
        // Honour the flush intent: do not sticky-preserve an earlier in-progress flag.
        ...(isThinkingInProgress ? { isThinkingInProgress: true } : {})
      }
    ]
  }

  return [
    ...contentParts,
    {
      type: 'thinkingSignatures',
      id: thinkingPartId(pendingThinkingSignatures),
      thinkingSignatures: pendingThinkingSignatures,
      ...(isThinkingInProgress ? { isThinkingInProgress: true } : {})
    }
  ]
}

const lastIndexOfPartType = (parts: AgUiContentPartDraft[], type: AgUiContentPartDraft['type']): number => {
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i].type === type) return i
  }
  return -1
}

/**
 * Merge ADK end-of-turn thought text into an existing thought without duplicating
 * a replayed segment (ADK often re-sends the same trace after the answer).
 * Only for overlapping/equal text — distinct thoughts stay separate.
 */
export const mergeReasoningThoughtContents = (existing: string, incoming: string): string => {
  if (incoming.length === 0) return existing
  if (existing.length === 0) return incoming
  if (existing === incoming || existing.includes(incoming)) return existing
  if (incoming.includes(existing)) return incoming
  return `${existing}\n${incoming}`
}

const areReasoningThoughtContentsOverlapping = (existing: string, incoming: string): boolean => {
  if (existing.length === 0 || incoming.length === 0) return false
  return existing === incoming || existing.includes(incoming) || incoming.includes(existing)
}

/**
 * Fold hoisted ADK trailing thoughts into the preceding signature list.
 * Only merge into the last thought when content overlaps (ADK replay); otherwise
 * append so Thought → Tool → Thought stays interleaved.
 */
const mergeTrailingThoughtsIntoSignatures = (
  preceding: CopilotThinkingSignature[],
  trailingThoughts: Array<Extract<CopilotThinkingSignature, { type: 'thought' }>>
): CopilotThinkingSignature[] => {
  const merged = [...preceding]
  for (const thought of trailingThoughts) {
    const last = merged.at(-1)
    if (last?.type !== 'thought') {
      merged.push(thought)
      continue
    }

    const existingContent = typeof last.content === 'string' ? last.content : ''
    const incomingContent = typeof thought.content === 'string' ? thought.content : ''
    if (!areReasoningThoughtContentsOverlapping(existingContent, incomingContent)) {
      merged.push(thought)
      continue
    }

    merged[merged.length - 1] = {
      ...last,
      content: mergeReasoningThoughtContents(existingContent, incomingContent),
      // Keep the preceding streak duration (first reasoning → next non-reasoning).
      timeInSeconds: last.timeInSeconds
    }
  }
  return merged
}

/**
 * ADK quirk: a final reasoning burst often arrives after assistant prose for the
 * same turn. Once settled, move trailing thought-only signatures above the last
 * `assistantContent` so they are not a second block below the answer. Replayed
 * text merges into the last thought; distinct text is appended as its own thought.
 *
 * Only runs when the bubble ends with thought signatures after prose; leaves
 * mid-stream (`isLoading`) ordering alone so in-progress UI stays honest.
 */
export const hoistTrailingAdkReasoningThoughts = (contentParts: AgUiContentPartDraft[]): AgUiContentPartDraft[] => {
  const trailing = contentParts.at(-1)
  if (trailing?.type !== 'thinkingSignatures') return contentParts

  const trailingThoughts = trailing.thinkingSignatures.filter(
    (signature): signature is Extract<CopilotThinkingSignature, { type: 'thought' }> => signature.type === 'thought'
  )
  // Do not hoist mixed trailing parts (e.g. search + thought) — only pure ADK thought bursts.
  if (trailingThoughts.length === 0 || trailingThoughts.length !== trailing.thinkingSignatures.length) {
    return contentParts
  }

  const lastAssistantIdx = lastIndexOfPartType(contentParts, 'assistantContent')
  if (lastAssistantIdx < 0 || contentParts.length - 1 <= lastAssistantIdx) {
    return contentParts
  }

  const withoutTrailing = contentParts.slice(0, -1)
  const precedingThinkingIdx = lastIndexOfPartType(withoutTrailing.slice(0, lastAssistantIdx + 1), 'thinkingSignatures')

  if (precedingThinkingIdx >= 0) {
    const preceding = withoutTrailing[precedingThinkingIdx]
    if (preceding.type !== 'thinkingSignatures') return contentParts

    const thinkingSignatures = mergeTrailingThoughtsIntoSignatures(preceding.thinkingSignatures, trailingThoughts)
    return withoutTrailing.map((part, index) =>
      index === precedingThinkingIdx
        ? {
            type: 'thinkingSignatures',
            id: thinkingPartId(thinkingSignatures),
            thinkingSignatures,
            ...(preceding.isThinkingInProgress === true ? { isThinkingInProgress: true } : {})
          }
        : part
    )
  }

  return [
    ...withoutTrailing.slice(0, lastAssistantIdx),
    {
      type: 'thinkingSignatures',
      id: thinkingPartId(trailingThoughts),
      thinkingSignatures: trailingThoughts
    },
    ...withoutTrailing.slice(lastAssistantIdx)
  ]
}

interface CopilotGroupState {
  messageId: string | null
  createdAt: Date | null
  contentParts: AgUiContentPartDraft[]
  isThinkingInProgress: boolean
  pendingThinkingSignatures: CopilotThinkingSignature[]
  /** Consecutive `role: 'reasoning'` messages, closed into one `thought` on the next non-reasoning event. */
  openReasoningStreak: OpenReasoningStreak | null
  pendingMessageEndToolCallIds: string[]
  chartIdMap: Map<string, CopilotChart>
}

const emptyCopilotGroup = (): CopilotGroupState => ({
  messageId: null,
  createdAt: null,
  contentParts: [],
  isThinkingInProgress: false,
  pendingThinkingSignatures: [],
  openReasoningStreak: null,
  pendingMessageEndToolCallIds: [],
  chartIdMap: new Map()
})

interface CopilotDraftReducerContext {
  toolCallsById: Record<string, AgUiToolCall>
  toolCallResultsMap: Record<string, ToolResultMessage>
  lastVisibleId: string | null
  isLoading: boolean
  pageElements: CopilotPageElement[] | undefined
  theme: Theme
  now: Date
  csvFilenameByGcsUri?: Readonly<Record<string, string>>
  reasoningEndedAtById?: ReadonlyMap<string, string>
}

const COAGENT_STATE_RENDER_NAME = 'coagent-state-render'
const COPILOT_PENDING_THINKING_ID = 'copilot-pending-thinking'
const MESSAGE_END_PLACEMENT = 'message_end'

/** HITL tools write these statuses via `sendToolResult` once the user decides. */
const TERMINAL_HITL_TOOL_RESULT_STATUSES = new Set(['approved', 'rejected', 'failed'])

/**
 * True when a message_end tool result is already settled (user decided).
 * Settled cards stay inline so a follow-up agent run does not hide them mid-stream.
 */
const hasTerminalHitlToolResult = (resultMessage: ToolResultMessage | undefined): boolean => {
  if (resultMessage == null) return false

  let parsed: unknown = resultMessage.content
  if (typeof resultMessage.content === 'string') {
    try {
      parsed = JSON.parse(resultMessage.content)
    } catch {
      return false
    }
  }

  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return false
  }

  const status = (parsed as Record<string, unknown>).status
  return typeof status === 'string' && TERMINAL_HITL_TOOL_RESULT_STATUSES.has(status)
}

/**
 * Mutable reducer that walks AG-UI messages into draft bubbles.
 * Extracted from `reduceAgUiMessagesToDrafts` to keep that entry point thin
 * for cognitive-complexity limits.
 */
class CopilotDraftReducer {
  private readonly ctx: CopilotDraftReducerContext
  private readonly output: AgUiMessageDraft[] = []
  private group: CopilotGroupState = emptyCopilotGroup()

  constructor(ctx: CopilotDraftReducerContext) {
    this.ctx = ctx
  }

  processMessage(message: AgUiMessage): void {
    if (message.role === 'assistant' && message.name === COAGENT_STATE_RENDER_NAME) return
    if (message.role === 'user') {
      this.handleUserMessage(message)
      return
    }
    if (message.role === 'tool') {
      this.handleToolMessage(message)
      return
    }
    if (message.role === 'reasoning') {
      this.handleReasoningMessage(message)
      return
    }
    if (message.role !== 'assistant') return
    this.handleAssistantMessage(message)
  }

  finalize(): AgUiMessageDraft[] {
    const { isLoading, now } = this.ctx
    this.flushCopilotMessage({ includeMessageEndTools: !isLoading })

    if (isLoading && this.output.at(-1)?.kind !== 'copilot') {
      this.output.push({
        kind: 'copilot',
        id: COPILOT_PENDING_THINKING_ID,
        createdAtIso: now.toISOString(),
        contentParts: [],
        isThinkingInProgress: true
      })
    }

    return this.output
  }

  private handleUserMessage(message: AgUiMessage & { role: 'user' }): void {
    const userDate = getMessageDate(message, this.ctx.now)
    this.closeOpenReasoningStreak(userDate)
    // Prior turns are complete even if a follow-up is still streaming.
    this.flushCopilotMessage({ settled: true })
    const attached = extractAttachedCsvFromUserContent(message.content)
    this.output.push({
      kind: 'user',
      id: message.id,
      createdAtIso: userDate.toISOString(),
      content: extractUserMessageText(message.content),
      attachedCsvFilename:
        attached == null ? null : resolveSessionRestoreFilename(attached.gcsUri, this.ctx.csvFilenameByGcsUri),
      attachedCsvGcsUri: attached?.gcsUri ?? null,
      attachedImages: extractAttachedImagesFromUserContent(message.content)
    })
  }

  private handleToolMessage(message: ToolResultMessage): void {
    this.closeOpenReasoningStreak(getMessageDate(message, this.ctx.now))

    const toolCall = this.ctx.toolCallsById[message.toolCallId]
    if (toolCall == null) return

    const nextChartMap = new Map(this.group.chartIdMap)
    addChartToMap(nextChartMap, extractChartFromToolResult(toolCall, message.content))
    addChartToMap(nextChartMap, extractDataChartFromToolResult(toolCall, message.content))
    this.group = { ...this.group, chartIdMap: nextChartMap }

    if (isCopilotVisibleTool(toolCall.function.name)) {
      this.enqueueOrRenderVisibleToolCall(toolCall)
    }
  }

  private handleReasoningMessage(message: ReasoningAgUiMessage): void {
    this.ensureCopilotMessage(message.id, message)
    const text = reasoningMessageText(message)
    const messageDate = getMessageDate(message, this.ctx.now)
    const frozenEndIso = this.ctx.reasoningEndedAtById?.get(message.id)
    const eventAt = frozenEndIso != null ? new Date(frozenEndIso) : messageDate
    const openStreak = this.group.openReasoningStreak

    if (openStreak == null) {
      this.group = {
        ...this.group,
        openReasoningStreak: {
          id: message.id,
          contentSegments: text.length > 0 ? [text] : [],
          startedAt: messageDate,
          lastEventAt: eventAt
        }
      }
      return
    }

    this.group = {
      ...this.group,
      openReasoningStreak: {
        ...openStreak,
        contentSegments: text.length > 0 ? [...openStreak.contentSegments, text] : openStreak.contentSegments,
        lastEventAt: eventAt
      }
    }
  }

  /**
   * Closes an open reasoning streak into a single `thought` signature.
   * Duration is first reasoning event → `endedAt` (next non-reasoning event, or `now` while still open).
   */
  private closeOpenReasoningStreak(endedAt: Date): void {
    const streak = this.group.openReasoningStreak
    if (streak == null) return

    if (streak.contentSegments.length === 0) {
      this.group = { ...this.group, openReasoningStreak: null }
      return
    }

    const thought: CopilotThinkingSignature = {
      id: streak.id,
      type: 'thought',
      timeInSeconds: reasoningStreakDurationSeconds(streak.startedAt, endedAt),
      content: streak.contentSegments.join('\n')
    }

    this.group = {
      ...this.group,
      openReasoningStreak: null,
      pendingThinkingSignatures: [...this.group.pendingThinkingSignatures, thought]
    }
  }

  private handleAssistantMessage(message: AssistantAgUiMessage): void {
    const { toolCallResultsMap, pageElements, theme, isLoading, lastVisibleId } = this.ctx
    this.closeOpenReasoningStreak(getMessageDate(message, this.ctx.now))
    this.ensureCopilotMessage(message.id, message)
    const toolCalls = message.toolCalls ?? []
    const nextChartMap = new Map(this.group.chartIdMap)
    toolCalls.forEach((toolCall) => {
      noteUnhandledCopilotTool(toolCall.function.name)
      addChartToMap(nextChartMap, extractChartFromToolCall(toolCall))
    })
    this.group = { ...this.group, chartIdMap: nextChartMap }

    const thinkingSignatures = extractThinkingSignatures(toolCalls, toolCallResultsMap, pageElements, theme)

    this.group = {
      ...this.group,
      pendingThinkingSignatures: [...this.group.pendingThinkingSignatures, ...thinkingSignatures],
      // Gated on isLoading: a run stopped mid-tool leaves its call without a
      // result for good, and must not keep the bubble "thinking" afterwards.
      isThinkingInProgress:
        this.group.isThinkingInProgress ||
        (isLoading && (hasPendingToolResults(toolCalls, toolCallResultsMap) || lastVisibleId === message.id))
    }

    if (isAssistantMessageWithGenerativeUi(message) && hasAssistantContentPart(message)) {
      this.addContentPart({
        type: 'assistantContent',
        id: `content-${message.id}`,
        messageId: message.id,
        chartEntries: [...this.group.chartIdMap.entries()]
      })
      return
    }

    // Tool-only assistants: commit pending thought/tool signatures now so a later
    // reasoning streak becomes a separate thought (Thought → Tool → Thought).
    if (this.group.pendingThinkingSignatures.length > 0) {
      this.flushPendingThinkingSignatures(this.group.isThinkingInProgress)
    }
  }

  private ensureCopilotMessage(messageId: string, message?: AgUiMessage): void {
    if (this.group.messageId != null) return
    this.group = {
      ...this.group,
      messageId,
      createdAt: message != null ? getMessageDate(message, this.ctx.now) : this.ctx.now
    }
  }

  private flushPendingThinkingSignatures(isThinkingInProgress = false): void {
    if (this.group.pendingThinkingSignatures.length === 0) return
    this.group = {
      ...this.group,
      contentParts: mergePendingThinkingSignatures(
        this.group.contentParts,
        this.group.pendingThinkingSignatures,
        isThinkingInProgress
      ),
      pendingThinkingSignatures: []
    }
  }

  private flushPendingMessageEndToolCalls(): void {
    if (this.group.pendingMessageEndToolCallIds.length === 0) return
    const nextParts = [...this.group.contentParts]
    for (const toolCallId of this.group.pendingMessageEndToolCallIds) {
      // Every unsettled message_end call must become a card. Hiding a same-name
      // duplicate would leave that toolCallId pending in the transcript while
      // confirming the visible card resumes the agent.
      if (this.hasVisibleToolPart(toolCallId, nextParts)) continue

      nextParts.push({
        type: 'visibleTool',
        id: `tool-call-${toolCallId}`,
        toolCallId
      })
    }
    this.group = {
      ...this.group,
      contentParts: nextParts,
      pendingMessageEndToolCallIds: []
    }
  }

  private flushCopilotMessage({
    includeMessageEndTools = true,
    settled = false
  }: { includeMessageEndTools?: boolean; settled?: boolean } = {}): void {
    // Live open group only: while loading, duration runs through `now`. Settled
    // groups (including prior turns flushed by a later user message) end at the
    // last reasoning event — `now` is a fresh Date() on every render.
    const treatAsLive = this.ctx.isLoading && !settled

    if (this.group.openReasoningStreak != null) {
      const endedAt = treatAsLive ? this.ctx.now : this.group.openReasoningStreak.lastEventAt
      this.closeOpenReasoningStreak(endedAt)
      if (treatAsLive) {
        this.group = { ...this.group, isThinkingInProgress: true }
      }
    }

    if (settled) {
      this.group = { ...this.group, isThinkingInProgress: false }
    }

    this.flushPendingThinkingSignatures(this.group.isThinkingInProgress)

    // Settled turns only: ADK often appends a final reasoning burst after prose.
    // Hoist it above the answer and merge into the earlier thought signature.
    if (!treatAsLive) {
      this.group = {
        ...this.group,
        contentParts: hoistTrailingAdkReasoningThoughts(this.group.contentParts)
      }
    }

    if (includeMessageEndTools) {
      this.flushPendingMessageEndToolCalls()
    }

    if (this.group.contentParts.length === 0 && !this.group.isThinkingInProgress) {
      this.group = emptyCopilotGroup()
      return
    }

    const createdAt = this.group.createdAt ?? this.ctx.now
    this.output.push({
      kind: 'copilot',
      id: this.group.messageId ?? COPILOT_PENDING_THINKING_ID,
      createdAtIso: createdAt.toISOString(),
      contentParts: this.group.contentParts,
      ...(this.group.isThinkingInProgress ? { isThinkingInProgress: true } : {})
    })

    this.group = emptyCopilotGroup()
  }

  private addContentPart(part: AgUiContentPartDraft): void {
    this.flushPendingThinkingSignatures()
    this.group = {
      ...this.group,
      contentParts: [...clearThinkingSignaturesInProgress(this.group.contentParts), part]
    }
  }

  private hasVisibleToolPart(
    toolCallId: string,
    contentParts: AgUiContentPartDraft[] = this.group.contentParts
  ): boolean {
    return contentParts.some((part) => part.type === 'visibleTool' && part.toolCallId === toolCallId)
  }

  private enqueueOrRenderVisibleToolCall(toolCall: AgUiToolCall): void {
    const resultMessage = this.ctx.toolCallResultsMap[toolCall.id]

    this.ensureCopilotMessage(toolCall.id)
    const placement = getCopilotVisibleToolPlacement(toolCall.function.name)
    const holdForMessageEnd = placement === MESSAGE_END_PLACEMENT && !hasTerminalHitlToolResult(resultMessage)

    if (holdForMessageEnd) {
      if (!this.group.pendingMessageEndToolCallIds.includes(toolCall.id)) {
        this.group = {
          ...this.group,
          pendingMessageEndToolCallIds: [...this.group.pendingMessageEndToolCallIds, toolCall.id]
        }
      }
      return
    }

    if (this.hasVisibleToolPart(toolCall.id)) {
      return
    }

    this.addContentPart({
      type: 'visibleTool',
      id: `tool-call-${toolCall.id}`,
      toolCallId: toolCall.id
    })
  }
}
/**
 * Reduce AG-UI messages into serializable drafts (see file overview).
 */
export const reduceAgUiMessagesToDrafts = ({
  messages,
  isLoading,
  pageElements,
  theme,
  now = new Date(),
  csvFilenameByGcsUri,
  reasoningEndedAtById
}: ReduceAgUiMessagesArgs): AgUiMessageDraft[] => {
  const reducer = new CopilotDraftReducer({
    toolCallsById: getToolCallsById(messages),
    toolCallResultsMap: getToolCallResultsById(messages),
    lastVisibleId: findLastVisibleAgUiMessageId(messages),
    isLoading,
    pageElements,
    theme,
    now,
    csvFilenameByGcsUri,
    reasoningEndedAtById
  })
  for (const message of messages) {
    reducer.processMessage(message)
  }
  return reducer.finalize()
}

export type { ToolResultMessage }
